import React, { useState, useEffect } from 'react';
import { FileText, Upload, Search } from 'lucide-react';
import DocumentList from './components/DocumentList';
import { UploadDocumentModal } from './components/UploadDocumentModal';
import BaseLayout from '../../layouts/BaseLayout';
import axios from 'axios';
import { useApiHandler } from '../../utils/apiHandler';
export default function DocumentsPage() {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const apiHandler = useApiHandler(); // Use the apiHandler hook

  // Fetch documents from the server when the component mounts
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await apiHandler.get('admin/document-list'); // Adjust API endpoint as needed
        setDocuments(response.data.data);
        setFilteredDocuments(response.data.data); // Initialize filteredDocuments
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };

    fetchDocuments();
  }, []);

  // Filter documents based on search query
  useEffect(() => {
    if (searchQuery) {
      setFilteredDocuments(
        documents.filter((doc) =>
          doc.documentTitle.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredDocuments(documents); // Reset filter if no search query
    }
  }, [searchQuery, documents]);

  const handleDocumentUpload = (newDocument) => {
    setDocuments((prevDocuments) => {
      const updatedDocuments = [...prevDocuments, newDocument];
      setFilteredDocuments(updatedDocuments); // Update filteredDocuments as well
      return updatedDocuments;
    });
  };

  const handleDeleteDocument = async (documentId) => {
    try {
        await apiHandler.post(`/admin/delete-document/${documentId}`);
        setDocuments((prevDocuments) => prevDocuments.filter(doc => doc._id !== documentId));
        setFilteredDocuments((prevFilteredDocuments) => 
          prevFilteredDocuments.filter(doc => doc._id !== documentId)
        );
        alert('Document deleted successfully');
    } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete the document');
    }
  };

  return (
    <BaseLayout>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-7 h-7 text-blue-600" />
                Documents
              </h1>
              <p className="mt-1 text-sm text-gray-500">Manage and share company documents</p>
            </div>
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Upload className="w-4 h-4" />
              Upload Document
            </button>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Document List */}
          <DocumentList 
              documents={filteredDocuments} // Pass filteredDocuments instead of documents
              onDeleteDocument={handleDeleteDocument} 
          />

          {/* Upload Modal */}
          <UploadDocumentModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            onDocumentUpload={handleDocumentUpload}
          />
        </div>
      </div>
    </BaseLayout>
  );
}
